import { useCallback, useMemo } from 'react';
import { gqlProfileStatusEnum as PROFILE_STATUS } from '@minecraft.graphql-types';
import { signUpQueryParams } from '@minecraft.utils';
import { getMarketingRedirect, removeMarketingRedirect } from '@blocs.auth';
import { useRouter } from '@blocs.router';
import { PaywallPreviewSubscriptionPurchaseQuery as PURCHASE } from '@minecraft.graphql-operations';

type RouterFunc<T extends any[]> = (...args: T) => { link: string };

const getRouterQuery = (isFromOnboarding: boolean): Record<string, string | Record<string, boolean>> => {
  return isFromOnboarding ? { linkQuery: `?isFromOnboarding=${isFromOnboarding}`, urlQuery: { isFromOnboarding } } : {};
};

/** Wraps a URL-building function so every route in the routeHelper registry has a uniform API (.link(), .build(), .forLink()). */
class RouteConstructor<T extends any[]> {
  func: RouterFunc<T>;

  constructor(func: RouterFunc<T>) {
    this.func = func;
  }

  /** Returns the full result object from the route function. */
  build(...args: T): { link: string } {
    return this.func(...args);
  }

  /** Returns the URL string for this route. Use with `push()` or as the `to` prop on a Link. */
  link(...args: T): string {
    return this.func(...args).link;
  }

  /** Legacy helper — wraps the URL in `{ to }` for spreading into Link props. Prefer `.link()` for new code. */
  forLink(...args: T): { to: string } {
    const generated = this.func(...args);

    return {
      to: generated.link,
    };
  }
}

/**
 * Route registry for consumer-talent. Each key maps a route name to a RouteConstructor
 * that knows how to build the URL for that route.
 *
 * @deprecated Use the helper derived from `appRoutes` in `src/routes/routeHelper.ts`.
 * Query-string support will be added to that helper separately.
 *
 * For URL generation without navigation: import routeHelper directly and call `.link()`:
 *   routeHelper.profile.link(profileId)    // → "/profile/abc123"
 *
 * For navigation (push) or when you also need the current location: use `useRouteHelper()`.
 */
const routeHelper = {
  acceptProfileAccess: new RouteConstructor((profileAccessToken: string) => ({
    link: `/accept-profile-access/${profileAccessToken}`,
  })),
  root: new RouteConstructor(() => ({
    link: '/',
  })),
  publicProfile: new RouteConstructor((guid: string) => ({
    link: `/public-profile/${guid}`,
  })),
  login: new RouteConstructor((queryParams?: URLSearchParams) => ({
    link: `/login?${queryParams ? queryParams.toString() : ''}`,
  })),
  project: new RouteConstructor((projectId: string | number, queryParams?: URLSearchParams) => ({
    link: `/project/${projectId}?${queryParams ? queryParams.toString() : ''}`,
  })),
  projectRole: new RouteConstructor(
    (projectId: string | number, roleId: string | number, queryParams?: URLSearchParams) => ({
      link: `/project/${projectId}/role/${roleId}?${queryParams ? queryParams.toString() : ''}`,
    })
  ),
  talentDisambiguation: new RouteConstructor(() => ({
    link: `/talents`,
  })),
  talentSettings: new RouteConstructor((artistId: number) => ({
    link: `/talents/${artistId}`,
  })),
  talentCopy: new RouteConstructor((profileId: string) => ({
    link: `/profiles/${profileId}/copy`,
  })),
  talentCopyFromRole: new RouteConstructor((profileId: string, projectId: string, roleId: string) => ({
    link: `/project/${projectId}/role/${roleId}/profiles/${profileId}/copy`,
  })),
  talentCopySelect: new RouteConstructor((profileId: string) => ({
    link: `/profiles/${profileId}/copy-select`,
  })),
  talentCopySelectFromRole: new RouteConstructor((profileId: string, projectId: string, roleId: string) => ({
    link: `/project/${projectId}/role/${roleId}/profiles/${profileId}/copy-select`,
  })),
  profileSubscriptions: new RouteConstructor(
    (profileId: string, { linkQuery = '' }: { linkQuery?: string } = {}, queryParams?: URLSearchParams) => ({
      link: `/profile/${profileId}/subscriptions${linkQuery}?${queryParams ? queryParams.toString() : ''}`,
    })
  ),
  profileSubscriptionSummary: new RouteConstructor(
    (profileId: string, planId: string, queryParams?: URLSearchParams) => ({
      link: `/profile/${profileId}/subscriptions/${planId}?${queryParams ? queryParams.toString() : ''}`,
    })
  ),
  profileCreateCheckout: new RouteConstructor(
    (profileId: string, purchase: PURCHASE, queryParams?: URLSearchParams) => ({
      link: `/profile/${profileId}/checkout?${queryParams ? queryParams.toString() : ''}`,
    })
  ),
  profilePurchaseSuccess: new RouteConstructor((profileId: string) => ({
    link: `/profile/${profileId}/purchase-success`,
  })),
  talentCreate: new RouteConstructor(() => ({
    link: `/talents/create`,
  })),
  talentNameSettings: new RouteConstructor((artistId: number | string) => ({
    link: `/talents/${artistId}/name`,
  })),
  artistDisambiguation: new RouteConstructor(() => ({
    link: `/artists`,
  })),
  artistSelect: new RouteConstructor((artistId: number | string) => ({
    link: `/artists/${artistId}`,
  })),
  artistProfiles: new RouteConstructor((artistId: number | string) => ({
    link: `/artists/${artistId}/profiles`,
  })),
  artistCreate: new RouteConstructor(() => ({
    link: '/artists/create',
  })),
  artistCreatePersonal: new RouteConstructor(() => ({
    link: '/artists/create-personal',
  })),
  profileVerifyAge: new RouteConstructor((queryParams?: URLSearchParams) => {
    return {
      link: `/profile/verify-age?${queryParams ? queryParams.toString() : ''}`,
    };
  }),
  artistProfile: new RouteConstructor(() => ({
    link: `/profile`,
  })),
  profileCreate: new RouteConstructor((profileId?: string, queryParams?: URLSearchParams) => ({
    link: profileId
      ? `/profile/create/${profileId}?${queryParams ? queryParams.toString() : ''}`
      : `/profile/create?${queryParams ? queryParams.toString() : ''}`,
  })),
  profileUploadVideo: new RouteConstructor((profileId: string) => ({
    link: `/profile/${profileId}/upload-video`,
  })),
  profileUploadAudio: new RouteConstructor((profileId: string) => ({
    link: `/profile/${profileId}/upload-audio`,
  })),
  profileUploadPhoto: new RouteConstructor((profileId: string) => ({
    link: `/profile/${profileId}/upload-photo`,
  })),
  profile: new RouteConstructor((profileId: string, { linkQuery = '' }: { linkQuery?: string } = {}) => ({
    link: `/profile/${profileId}${linkQuery}`,
  })),
  forwardRequest: new RouteConstructor((forwardRequestName: string, forwardRequestToken: string) => ({
    link: `/${forwardRequestName}/${forwardRequestToken}`,
  })),
  profileEdit: new RouteConstructor((profileId, editView) => ({
    link: `/profile/${profileId}/${editView}`,
  })),
  castingBillboard: new RouteConstructor(({ linkQuery = '' } = {}) => ({
    link: `/casting-billboard${linkQuery}`,
  })),
  castingBillboardSavedSearches: new RouteConstructor(({ linkQuery = '' } = {}) => ({
    link: `/casting-billboard/saved-searches${linkQuery}`,
  })),
  profileSettings: new RouteConstructor((artistId, profileId) => ({
    link: `/profile-settings/talents/${artistId}/profiles/${profileId}`,
  })),
  profileSettingsTalentProfiles: new RouteConstructor((artistId) => ({
    link: `/profile-settings/talents/${artistId}/profiles`,
  })),
  profileSettingsTalents: new RouteConstructor(() => ({
    link: '/profile-settings/talents',
  })),
  membershipOverview: new RouteConstructor((editSuccess?: boolean) => ({
    link: '/membership-billing',
  })),
  editBillingInfo: new RouteConstructor(() => ({
    link: '/membership-billing/edit-billing-info',
  })),
  editMembershipSubscriptionSummary: new RouteConstructor((artistId: number | string, planId: string) => ({
    link: `/membership-billing/${artistId}/subscription-summary/${planId}`,
  })),
  editMembershipCreateCheckout: new RouteConstructor((artistId, purchase: PURCHASE) => ({
    link: `/membership-billing/${artistId}/checkout`,
  })),
  editMembershipRenewal: new RouteConstructor((artistId, purchase: PURCHASE) => ({
    urlObject: { pathname: '/membership-billing/checkout', query: { artistId } },
    link: `/membership-billing/${artistId}/checkout`,
  })),
  editMembershipPurchaseSuccess: new RouteConstructor((artistId) => ({
    link: `/membership-billing/${artistId}/purchase-success`,
  })),
  mediaBank: new RouteConstructor((mediaType?: string) =>
    mediaType
      ? {
          link: `/media-bank/${mediaType}`,
        }
      : {
          link: '/media-bank',
        }
  ),
  verifyEmail: new RouteConstructor(() => ({
    link: '/verify-email',
  })),
  verifyEmailCode: new RouteConstructor((verificationId) => ({
    link: `/verify-email?verificationId=${verificationId}`,
  })),
  accountDetails: new RouteConstructor(() => ({
    link: '/account-settings/details',
  })),
  notificationSettings: new RouteConstructor(() => ({
    link: '/account-settings/notifications',
  })),
  artistNotificationSettings: new RouteConstructor((artistId) => ({
    link: `/account-settings/notifications/${artistId}`,
  })),
  artistProjectsNotificationSettings: new RouteConstructor((artistId) => ({
    link: `/account-settings/notifications/${artistId}/projects`,
  })),
  yourAlerts: new RouteConstructor((queryParams?: URLSearchParams) => ({
    link: `/your-alerts?${queryParams ? queryParams.toString() : ''}`,
  })),
  headshots: new RouteConstructor(() => ({
    link: '/headshots',
  })),
  rosterInvite: new RouteConstructor((rosterInviteToken) => ({
    link: `/roster-invite/${rosterInviteToken}`,
  })),
  forwardedRequest: new RouteConstructor((forwardedRequestData) => ({
    link: `/${forwardedRequestData.type}/${forwardedRequestData.token}`,
  })),
  questionRequest: new RouteConstructor((requestId: string) => ({
    link: `/request/${requestId}`,
  })),
  mediaRequest: new RouteConstructor((requestId: string) => ({
    link: `/media-request/${requestId}`,
  })),
  auditionRequest: new RouteConstructor((requestId: string) => ({
    link: `/audition-invite/${requestId}`,
  })),
  callbackRequest: new RouteConstructor((requestId: string) => ({
    link: `/callback-invite/${requestId}`,
  })),
  defaultRedirect: new RouteConstructor(() => ({
    link: `/default-redirect`,
  })),
  customizeSubmission: new RouteConstructor((projectId: string | number, roleId: string | number) => ({
    link: `/project/${projectId}/role/${roleId}/customize-submission`,
  })),
  submissionCompletion: new RouteConstructor((projectId: string | number, roleId: string | number) => ({
    link: `/project/${projectId}/role/${roleId}/submission-completion`,
  })),
};

type RouteHelperType = typeof routeHelper;
export type RouteHelperRoute = keyof RouteHelperType;
type RouteHelperParameters<Route extends RouteHelperRoute> = Parameters<RouteHelperType[Route]['link']>;

interface GenerateDefaultLinkOpts {
  profileId?: string;
  artistId?: number;
  profileStatusCode?: string;
  isPersonal?: boolean;
  isLoggedIn?: boolean;
  rosterInvitationToken?: string;
  forwardedRequestTokenData?: Record<string, any> | any[];
  artistHasRepProfile?: boolean;
  shouldCreatePersonalProfile?: boolean;
}
type GenerateDefaultLinkFn = (
  options: GenerateDefaultLinkOpts,
  withRedirect?: boolean,
  isFromOnboarding?: boolean
) => string;

/** Resolves the default landing page URL based on the user's auth state, profile status, and onboarding progress. */
export const generateDefaultLink: GenerateDefaultLinkFn = (
  {
    profileId,
    artistId,
    profileStatusCode,
    isPersonal,
    isLoggedIn,
    rosterInvitationToken,
    forwardedRequestTokenData,
    artistHasRepProfile,
    shouldCreatePersonalProfile,
  },
  isFromOnboarding = false
): string => {
  if (isLoggedIn) {
    if (forwardedRequestTokenData) {
      return routeHelper.forwardedRequest.link(forwardedRequestTokenData);
    }

    if (profileId) {
      if (shouldCreatePersonalProfile) {
        removeMarketingRedirect();

        return routeHelper.artistCreatePersonal.link();
      }

      const redirectUrl = getMarketingRedirect();
      const onboardingQuery = getRouterQuery(isFromOnboarding);

      // check the profile status code and redirect to the correct page this is for returning users who likely left
      // the site before completing their profile
      switch (profileStatusCode) {
        case PROFILE_STATUS.VERIFY_AGE:
          return routeHelper.profileCreate.link(profileId);
        case PROFILE_STATUS.CREATE_PROFILE_STEP_1:
          return routeHelper.profileCreate.link(profileId);
        case PROFILE_STATUS.CREATE_PROFILE_STEP_2:
          return routeHelper.profileUploadPhoto.link(profileId);
        case PROFILE_STATUS.CREATE_PROFILE_STEP_3:
          return routeHelper.profileUploadVideo.link(profileId);
        case PROFILE_STATUS.CREATE_PROFILE_STEP_4:
          return routeHelper.profileUploadAudio.link(profileId);
        case PROFILE_STATUS.SELECT_MEMBERSHIP:
          return routeHelper.profileSubscriptions.link(
            profileId,
            onboardingQuery,
            signUpQueryParams.queryParamsSelectMembership
          );
        case PROFILE_STATUS.ACTIVE:
          if (redirectUrl) {
            removeMarketingRedirect();

            return redirectUrl;
          }

          if (artistHasRepProfile) {
            return routeHelper.profile.link(profileId, onboardingQuery);
          }

          return isPersonal
            ? routeHelper.castingBillboard.link(onboardingQuery)
            : routeHelper.profile.link(profileId, onboardingQuery); // I don't think this path can ever be hit, it would be for a non-personal profile on an artist with no repped profiles
        default:
          return routeHelper.profileSettings.link(artistId, profileId);
      }
    } else {
      if (rosterInvitationToken) {
        return routeHelper.rosterInvite.link(rosterInvitationToken);
      }

      return routeHelper.profileCreate.link(null, signUpQueryParams.registrationStepIds.verifyAge);
    }
  }

  return routeHelper.root.link();
};

export default routeHelper;

/**
 * Hook for navigating between consumer-talent routes.
 *
 * @deprecated Use the helper derived from `appRoutes` in `src/routes/routeHelper.ts`.
 * Query-string support will be added to that helper separately.
 *
 * Returns:
 *  - `link(route, ...params)` — build a URL string without navigating
 *  - `push(route, ...params)` — navigate to a named route
 *  - `contextPush(url)` — navigate to an arbitrary URL string
 *  - `location` — current `{ pathname, search }` from the router
 *
 * For reading route/search params or other router state, use `useRouter()` from `@blocs.router` directly.
 */
export const useRouteHelper = () => {
  const { navigate, pathname, search } = useRouter();

  const location = useMemo(() => ({ pathname, search }), [pathname, search]);

  const link = useCallback(<Route extends RouteHelperRoute>(route: Route, ...params: RouteHelperParameters<Route>) => {
    return (routeHelper[route] as RouteConstructor<any[]>).link(...params);
  }, []);

  const push = useCallback(
    <Route extends RouteHelperRoute>(route: Route, ...params: RouteHelperParameters<Route>) => {
      const { link: routeHelperLink } = (routeHelper[route] as RouteConstructor<any[]>).build(...params);
      navigate({ to: routeHelperLink });
    },
    [navigate]
  );

  const contextPush = useCallback((url: string) => navigate({ to: url }), [navigate]);

  return useMemo(
    () => ({
      location,
      link,
      push,
      contextPush,
    }),
    [location, link, push, contextPush]
  );
};
